import { Modal } from '@/shared/ui/Modal'
import { Flag, LanguageFlag } from '@/shared/places/Flag'
import { usePlaces } from '@/shared/places/customerFacts'
import type { AccessGrantRow } from './repository/accessGrantsRepository'
import { formatDate } from '@/shared/util/dates'
import { PromoCodeHeader } from './PromoCodePanel'

const KIND_LABEL: Record<AccessGrantRow['kind'], string> = {
  'promo-code': 'Promo code',
  granted: 'Granted entitlement',
  partner: 'Partner programme',
}

/**
 * Who received access through one grant.
 *
 * The only view in the CRM showing consumer names and email addresses. The rows
 * arrive with the table, so opening this costs no extra reads, and the exposure
 * is bounded by that grant's size rather than the user base.
 */
export function GrantPeopleModal({
  grant,
  onClose,
  canEdit = false,
  onChanged,
}: {
  grant: AccessGrantRow
  onClose: () => void
  /** Promo codes: whether this person may edit the code. */
  canEdit?: boolean
  /** Promo codes: the code was saved; reload the table. */
  onChanged?: () => void
}) {
  const people = grant.people
  // Country and language per person, from RevenueCat — 28 Oct 2026.
  const places = usePlaces(people.map((p) => p.userId))
  const live = people.filter((p) => p.live).length

  return (
    <Modal
      title={grant.label}
      subtitle={`${KIND_LABEL[grant.kind]}${grant.detail ? ` · ${grant.detail}` : ''} · ${grant.total} granted`}
      width={grant.promo ? 720 : 620}
      onClose={onClose}
      footer={
        <button className="btn btn--ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      {grant.promo && (
        <PromoCodeHeader promo={grant.promo} canEdit={canEdit} onChanged={() => onChanged?.()} />
      )}

      {people.length === 0 && <div className="empty-note">Nobody has redeemed this grant yet. Names appear here as people use the code.</div>}

      {people.length > 0 && (
        <>
          <div className="redeem__summary">
            {live} of {people.length} still have access
          </div>

          <div className="redeem__head">
            <div>Name</div>
            <div>Email</div>
            <div title="Where RevenueCat last saw the account">Country</div>
            <div title="The language Skida writes in for that country">Language</div>
            <div>{grant.kind === 'promo-code' ? 'Redeemed' : 'Since'}</div>
            <div>Status</div>
          </div>

          <div className="redeem__list">
            {people.map((p) => (
              <div className="redeem__row" key={p.userId}>
                <div className="redeem__name">{p.name || <span className="muted">—</span>}</div>
                <div className="redeem__email">{p.email || <span className="muted">—</span>}</div>
                <div>
                  <Flag code={places.get(p.userId)?.country} />
                </div>
                <div>
                  <LanguageFlag language={places.get(p.userId)?.language} />
                </div>
                <div className="muted">{p.at ? formatDate(p.at) : '—'}</div>
                <div>
                  {!p.exists ? (
                    <span className="pill pill--neutral">Account deleted</span>
                  ) : p.live ? (
                    <span className="pill pill--success">
                      Premium{p.periodType === 'TRIAL' ? ' (trial)' : ''}
                    </span>
                  ) : (
                    <span className="pill pill--neutral">Expired</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="micro redeem__note">
            Status is read from the entitlement's expiry, not the <code>isActive</code> flag, which
            is never cleared and would mark lapsed accounts as active. Consumer personal data —
            handle it the way you would any customer list.
          </div>
        </>
      )}
    </Modal>
  )
}
